import React from 'react'

/** @jsx jsx */
import {jsx, css} from '@emotion/react'
import {Helmet} from 'react-helmet'
import PropTypes from 'prop-types'
import {
    Flex,
    Text,
    List,
    ListItem,
    Box,
    Link,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PDCTitle from '../../components/pdc-title/index.jsx'
import DynamicImage from '@app/components/dynamic-image'
import usePDCResponsiveElement from '../../hooks/use-pdc-responsive-element.js'
import {usePage} from '@salesforce/commerce-sdk-react'
import {
    DYNAMIC_IMAGE_WIDTHS,
    DISPATCH_COUNTRIES,
    CART_HREF,
    HOME_HREF,
    TURKEY_PARTNER_SITE,
    RUSSIA_PARTNER_SITE
} from '../../constants.js'
import {DISPATCH_COUNTRIES_MESSAGES} from './constants.js'

import LoadingSpinner from '@app/components/loading-spinner'
import {BrandLogo, BrandLogoWhite} from '../../components/custom-icons'
import ReadMore from '../../components/read-more/index.jsx'
import {
    getEnvStaleTime,
    clearLocalStorageJSONItem,
    getClosestLocale,
    getPageDesignerRegion
} from '../../utils/utils'

// Custom hooks
import useMultiSite from '../../hooks/use-multi-site'
import {useGiftCardLogic} from '@app/hooks/use-gift-card-logic'
import {useCurrentBasket} from '@app/hooks/use-current-basket'
import {useGiftCardActions} from '@app/hooks/use-gift-card-actions'
import {useGTM} from '@app/hooks/use-gtm'
import {useApplyShipToCountry} from '@app/hooks/use-apply-ship-to-country'

// OOTB config
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// Custom Utils
import {getShipToCountry} from '@app/utils/ship-to-country-utils'

// third Parties
import {useIntl} from 'react-intl'

/**
 * CountryDispatch Component
 *
 * This component is responsible for rendering a section of the application that allows users to select a country
 * for dispatch purposes. It utilizes various hooks and functions to determine the appropriate locale and image
 * based on the user's selection and the current site configuration. The component is structured to display a
 * dynamic image and a list of continents and countries, enabling users to choose their desired country for
 * delivery.
 *
 * The component leverages responsive design principles to adapt to different screen sizes, ensuring that the
 * layout remains user-friendly across devices. It also handles user interactions, such as selecting a country
 * from a dropdown, and updates the application state accordingly.
 *
 * Key Features:
 * - Dynamically fetches and displays images based on the current breakpoint.
 * - Determines the appropriate locale for the selected country.
 * - Handles country selection and updates the shopper's context.
 * - Provides a responsive layout that adapts to various screen sizes.
 *
 * Dependencies:
 * - useMultiSite: A hook to retrieve the current locale and site information.
 * - usePage: A hook to fetch page data based on specific parameters.
 * - useBreakpointValue: A hook to determine the current image based on screen size.
 * - usePDCResponsiveElement: A utility to manage responsive elements for images.
 *
 * @component
 */
export const CountryDispatch = () => {
    const {locale, isOutletSite} = useMultiSite()
    const staleTime = getEnvStaleTime()
    const {app: appConfig} = getConfig()
    const intl = useIntl()
    const {applyShipToCountry} = useApplyShipToCountry()
    const {data: basket} = useCurrentBasket()
    const {
        isGCAvailableForCurrentCountry,
        isGCAvailableForCurrency,
        checkIfThereAnyGCInBasket,
        checkGiftCardCurrencyMismatch
    } = useGiftCardLogic()
    const {removeAllGiftCardPaymentInstruments} = useGiftCardActions(basket)

    const {data, isFetching} = usePage(
        {parameters: {pageId: 'deliverycountrydispatchpage', locale: locale?.id}},
        {staleTime: staleTime, keepPreviousData: true}
    )
    const {pageTitle, pageDescription, pageKeywords} = data || {}

    const component = getPageDesignerRegion(data, 'main')?.components?.find(
        (comp) => comp?.typeId === 'commerce_layouts.pdcCountryDispatch'
    )
    const componentData = component?.data
    const componentCustom = component?.custom

    const initialSeoText = componentData?.initialSeoText
    const secondarySeoText = componentData?.secondarySeoText
    const readMoreSeoText = componentData?.readMoreText

    // Get current image based on breakpoint
    const currentImage = useBreakpointValue(
        usePDCResponsiveElement({
            smallEl: componentCustom?.smallImage?.disBaseLink || componentData?.smallImage,
            mediumEl: componentCustom?.mediumImage?.disBaseLink || componentData?.mediumImage,
            largeEl: componentCustom?.largeImage?.disBaseLink || componentData?.largeImage,
            xlargeEl: componentCustom?.xlargeImage?.disBaseLink || componentData?.xlargeImage,
            xxlargeEl: componentCustom?.xxlargeImage?.disBaseLink || componentData?.xxlargeImage
        })
    )

    /**
     * Determines the appropriate locale based on the provided country code and site configuration.
     *
     * @param {string} countryCode - The country code used to determine the target site and locale.
     * @returns {string} - The final locale string, stripped of any region code, that matches the browser language or the default locale if no match is found.
     *
     * The function uses the following logic:
     * - Identifies the target site based on the country code ('US' maps to 'VBQ-US', others to 'VBQ-EU').
     * - Retrieves the browser language code from the client locale or the current locale.
     * - If the target site matches the current site, it attempts to use the browser language if supported.
     * - If the target site is different, it checks for a partial match with the browser language among the target site's supported locales.
     * - If no match is found, it defaults to the target site's default locale.
     * - Finally, it removes any region code from the locale before returning it.
     */
    const getLocale = (countryCode) => {
        const targetSiteId = countryCode === 'US' ? 'VBQ-US' : 'VBQ-EU'
        const targetSite = appConfig?.sites.find((s) => s.id === targetSiteId)

        // Get closest locale, prioritizing the currently selected locale
        const localeProps = getClosestLocale(targetSite, [
            locale.id,
            ...targetSite.l10n.supportedLocales.map((locale) => locale.id)
        ])

        return localeProps.alias
    }

    /**
     * Handles the selection of a country from a dropdown or similar UI element.
     *
     * This asynchronous function is triggered when a user selects a country. It retrieves
     * the country code from the event target, determines the shipping details for the selected
     * country, and updates the shopper's context with the new country code and currency.
     * It also navigates the user to the men's category page.
     *
     * @async
     * @function onCountrySelect
     * @param {Object} element - The event object from the country selection UI element.
     * @param {Object} element.target - The target element of the event.
     * @param {string} element.target.id - The ID of the target element, representing the country code.
     *
     * @returns {Promise<void>} - A promise that resolves when the function completes its operations.
     */
    const onCountrySelect = async (e) => {
        e.preventDefault()

        const countryCode = e.currentTarget.id
        const url = e.currentTarget.href

        /**
         * If the user used gift certificates as payment method,
         * we need to remove them when we change the country.
         */
        removeAllGiftCardPaymentInstruments()

        if (!['TR', 'RU'].includes(countryCode)) {
            await applyShipToCountry(countryCode, {
                accepted: true,
                triggerGtm: false,
                updateBasketAssignments: false
            })
        }

        clearLocalStorageJSONItem('newsletterModalUsed')
        window.location.href = url
    }

    const styles = {
        _after: {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.2)'
        }
    }

    return (
        <Box position="relative">
            {isFetching && <LoadingSpinner />}

            <Helmet>
                {pageTitle && <title>{pageTitle}</title>}
                {pageDescription && <meta name="description" content={pageDescription} />}
                {pageKeywords && <meta name="keywords" content={pageKeywords} />}
            </Helmet>

            <Box
                as="section"
                display="flex"
                flexDirection={['column', 'column', 'row']}
                justifyContent="space-between"
                alignItems="stretch"
                width="100%"
                height={'auto'}
            >
                <Box width={['100%', null, '50%']} position="relative">
                    <Box position="sticky" top="0">
                        {currentImage && (
                            <DynamicImage
                                src={`${
                                    typeof currentImage === 'string'
                                        ? currentImage
                                        : currentImage?.url
                                }[?sw={width}]`}
                                widths={DYNAMIC_IMAGE_WIDTHS.halfandfullonsmalldevice}
                                position="relative"
                                {...styles}
                                imageProps={{
                                    width: '100%',
                                    loading: 'auto',
                                    fetchpriority: 'high',
                                    alt:
                                        componentData?.imageAlt === 'empty'
                                            ? ''
                                            : componentData?.imageAlt || componentData?.title,
                                    title:
                                        componentData?.imageTitle === 'empty'
                                            ? ''
                                            : componentData?.imageTitle || componentData?.title
                                }}
                            />
                        )}
                        <Flex
                            alignItems="center"
                            justifyContent="center"
                            direction={'column'}
                            position="absolute"
                            top="0"
                            left="0"
                            width="100%"
                            p={'34px'}
                        >
                            {componentData?.isLogoTransparent ? (
                                <BrandLogoWhite width="94px" height="auto" />
                            ) : (
                                <BrandLogo width="94px" height="auto" />
                            )}
                            <PDCTitle
                                title={componentData?.title}
                                titleFontColor={componentData?.titleFontColor || 'white'}
                                titleHeadingLevel={componentData?.titleHeadingLevel}
                                txtHorizontalPos={'center'}
                                size="4xl"
                                mt="50px"
                            />
                        </Flex>
                    </Box>
                </Box>
                <Box
                    width={['100%', null, '50%']}
                    p={['31px 18px', null, '41px 38px', '76px 0 51px 71px']}
                    position="relative"
                >
                    <Text
                        dangerouslySetInnerHTML={{
                            __html: componentData?.subTitle
                        }}
                        variant="eyeBrowLarge"
                        color={'fullBlack'}
                        mb={'25px'}
                    />
                    <List sx={{columns: {base: '2', lg: '3'}}}>
                        {DISPATCH_COUNTRIES.map((continent) => {
                            const countryList = continent.countries.toSorted((a, b) =>
                                intl
                                    .formatMessage(
                                        DISPATCH_COUNTRIES_MESSAGES[
                                            a.name.toLowerCase().replace(/\s+/g, '_')
                                        ]
                                    )
                                    .localeCompare(
                                        intl.formatMessage(
                                            DISPATCH_COUNTRIES_MESSAGES[
                                                b.name.toLowerCase().replace(/\s+/g, '_')
                                            ]
                                        )
                                    )
                            )

                            return (
                                <ListItem key={continent.continent} mb="12px">
                                    <Text
                                        color={'fullBlack'}
                                        mb={'2px'}
                                        fontFamily={'heading'}
                                        fontSize={'3xl'}
                                        as={'h2'}
                                    >
                                        {intl.formatMessage(
                                            DISPATCH_COUNTRIES_MESSAGES[
                                                continent.continent
                                                    .toLowerCase()
                                                    .replace(/\s+/g, '_')
                                            ]
                                        )}
                                    </Text>

                                    <List>
                                        {countryList.map((country) => {
                                            let url

                                            const countryUrls = new Map([
                                                ['TR', TURKEY_PARTNER_SITE],
                                                ['RU', RUSSIA_PARTNER_SITE]
                                            ])

                                            if (countryUrls.has(country.code)) {
                                                url = countryUrls.get(country.code)
                                            } else {
                                                const region = isOutletSite
                                                    ? country.code === 'US'
                                                        ? 'outlet-us'
                                                        : 'outlet-eu'
                                                    : country.code === 'US'
                                                    ? 'us'
                                                    : 'eu'
                                                const locale = getLocale(country.code)
                                                const base = `/${region}/${locale}`

                                                const shipToCountry = getShipToCountry(
                                                    country.code,
                                                    country.code === 'US'
                                                )
                                                const hasGcLineItem =
                                                    checkIfThereAnyGCInBasket(basket)
                                                const gcNotAvailableInCountry =
                                                    !isGCAvailableForCurrentCountry(
                                                        shipToCountry?.id
                                                    )
                                                const gcNotAvailableForCurrency =
                                                    !isGCAvailableForCurrency(
                                                        shipToCountry?.currency
                                                    )

                                                /**
                                                 * If the user is buying gift cards, but the new country does not support them,
                                                 * or it supports them, but with a different currency, go to the cart page, where this is handled
                                                 */
                                                const shouldRedirectToCart =
                                                    hasGcLineItem &&
                                                    (gcNotAvailableInCountry ||
                                                        gcNotAvailableForCurrency ||
                                                        checkGiftCardCurrencyMismatch(
                                                            basket?.giftCertificateItems,
                                                            shipToCountry?.currency
                                                        ))

                                                // Use the extracted function to determine the URL
                                                url = shouldRedirectToCart
                                                    ? `${base}${CART_HREF}`
                                                    : `${base}${HOME_HREF}`
                                            }

                                            return (
                                                <ListItem key={country.code} display={'flex'}>
                                                    <Link
                                                        onClick={onCountrySelect}
                                                        id={country.code}
                                                        textDecoration={'none'}
                                                        display={'inline-block'}
                                                        href={url}
                                                    >
                                                        <Text
                                                            as="span"
                                                            variant="touchFriendly"
                                                            color="darkGray"
                                                            pointerEvents={'none'}
                                                        >
                                                            {intl.formatMessage(
                                                                DISPATCH_COUNTRIES_MESSAGES[
                                                                    country.name
                                                                        .toLowerCase()
                                                                        .replace(/\s+/g, '_')
                                                                ]
                                                            )}
                                                        </Text>
                                                    </Link>
                                                </ListItem>
                                            )
                                        })}
                                    </List>
                                </ListItem>
                            )
                        })}
                    </List>
                    {initialSeoText && (
                        <ReadMore
                            initialSeoText={initialSeoText}
                            secondarySeoText={secondarySeoText}
                            readMoreSeoText={readMoreSeoText}
                        />
                    )}
                </Box>
            </Box>
        </Box>
    )
}

CountryDispatch.css = css`
    .chakra-link {
        padding: 18px 20px;
        text-decoration: none;
    }
`

CountryDispatch.propTypes = {
    title: PropTypes.string,
    titleHeadingLevel: PropTypes.string,
    titleFontColor: PropTypes.string,
    subTitle: PropTypes.string,
    isLogoTransparent: PropTypes.bool,
    smallImage: PropTypes.object,
    mediumImage: PropTypes.object,
    largeImage: PropTypes.object,
    xlargeImage: PropTypes.object,
    xxlargeImage: PropTypes.object,
    imageTitle: PropTypes.string,
    imageAlt: PropTypes.string,
    regions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    data: PropTypes.object,
                    id: PropTypes.string,
                    typeId: PropTypes.string
                })
            )
        })
    )
}

export default CountryDispatch
